// src/components/Header.jsx

import React, { useState, useEffect } from "react";
import { FaBars, FaTimes, FaPhoneAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/jainlogos.png";

/* ─── Injected CSS ────────────────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Inter:wght@400;500;600&display=swap');

.jbh-header {
  position: fixed;
  top: 0; left: 0;
  width: 100%;
  z-index: 100;
  font-family: 'Inter', sans-serif;
  transition: background 0.3s, box-shadow 0.3s;
}

/* scrolled: rich maroon with gold underline */
.jbh-header.scrolled {
  background: linear-gradient(90deg, #5A1010 0%, #7B1C1C 100%);
  box-shadow: 0 2px 0 #C9962A, 0 4px 20px rgba(0,0,0,0.45);
}

/* top: transparent */
.jbh-header.top {
  background: rgba(90,16,16,0.82);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 1px 0 rgba(201,150,42,0.4);
}

.jbh-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 clamp(12px, 3vw, 32px);
  height: clamp(56px, 8vw, 68px);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

/* ── Logo area ── */
.jbh-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  text-decoration: none;
  flex-shrink: 0;
}
.jbh-logo img {
  height: clamp(36px, 5vw, 48px);
  width: auto;
  border-radius: 50%;
  border: 1.5px solid #C9962A;
  box-shadow: 0 0 0 2px rgba(201,150,42,0.2);
}
.jbh-logo-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.jbh-logo-name {
  font-family: 'Playfair Display', serif;
  font-size: clamp(13px, 1.6vw, 17px);
  font-weight: 700;
  color: #FDF6EC;
  letter-spacing: 0.2px;
}
.jbh-logo-tagline {
  font-size: clamp(9px, 1vw, 11px);
  color: #C9962A;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  font-weight: 500;
}

/* hide name on very small screens */
@media (max-width: 360px) {
  .jbh-logo-text { display: none; }
}

/* ── Phone button ── */
.jbh-phone {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 6px 12px;
  border: 1px solid rgba(201,150,42,0.55);
  border-radius: 8px;
  color: #E8C46A;
  font-size: clamp(11px, 1.2vw, 13px);
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  transition: border-color 0.18s, background 0.18s;
  flex-shrink: 0;
}
.jbh-phone:hover { border-color: #C9962A; background: rgba(201,150,42,0.1); }
.jbh-phone svg  { color: #C9962A; font-size: 13px; flex-shrink: 0; }

/* hide phone number text on mobile, show icon only */
.jbh-phone-num { display: inline; }
@media (max-width: 520px) { .jbh-phone-num { display: none; } }

/* ── Desktop nav (right side) ── */
.jbh-nav-desktop {
  display: flex;
  align-items: center;
  gap: 10px;
}
@media (max-width: 767px) { .jbh-nav-desktop { display: none; } }

/* ── Buttons ── */
.jbh-btn-register {
  padding: 7px 18px;
  background: linear-gradient(90deg, #C9962A, #A87820);
  border: none;
  border-radius: 20px;
  color: #2C1A0E;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  letter-spacing: 0.3px;
  font-family: 'Inter', sans-serif;
  transition: opacity 0.18s;
  white-space: nowrap;
}
.jbh-btn-register:hover { opacity: 0.88; }

.jbh-btn-login {
  padding: 6px 16px;
  background: transparent;
  border: 1.5px solid #C9962A;
  border-radius: 20px;
  color: #E8C46A;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  transition: background 0.18s;
  white-space: nowrap;
}
.jbh-btn-login:hover { background: rgba(201,150,42,0.12); }

.jbh-btn-logout {
  padding: 6px 16px;
  background: transparent;
  border: 1.5px solid rgba(255,120,120,0.7);
  border-radius: 20px;
  color: #FFAAAA;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  transition: background 0.18s;
  white-space: nowrap;
}
.jbh-btn-logout:hover { background: rgba(255,80,80,0.1); }

/* ── Hamburger ── */
.jbh-hamburger {
  display: none;
  background: none;
  border: none;
  color: #E8C46A;
  cursor: pointer;
  padding: 4px;
  flex-shrink: 0;
}
@media (max-width: 767px) { .jbh-hamburger { display: flex; align-items: center; } }

/* ── Mobile auth button (always visible on mobile) ── */
.jbh-mobile-auth { display: none; }
@media (max-width: 767px) { .jbh-mobile-auth { display: flex; align-items: center; } }

/* ══════════════════════════════════════
   SLIDE-IN DRAWER
══════════════════════════════════════ */
.jbh-drawer {
  position: fixed;
  top: 0; right: 0;
  height: 100%;
  width: min(280px, 85vw);
  background: linear-gradient(160deg, #5A1010 0%, #3D0A0A 100%);
  border-left: 1px solid rgba(201,150,42,0.3);
  box-shadow: -8px 0 40px rgba(0,0,0,0.5);
  transform: translateX(100%);
  transition: transform 0.3s cubic-bezier(.4,0,.2,1);
  z-index: 200;
  display: flex;
  flex-direction: column;
}
.jbh-drawer.open { transform: translateX(0); }

.jbh-drawer-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(201,150,42,0.25);
}
.jbh-drawer-title {
  font-family: 'Playfair Display', serif;
  font-size: 15px;
  font-weight: 700;
  color: #FDF6EC;
}
.jbh-drawer-sub {
  font-size: 10px;
  color: #C9962A;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-top: 2px;
}
.jbh-drawer-close {
  background: none;
  border: none;
  color: #E8C46A;
  cursor: pointer;
  padding: 4px;
}

/* Om watermark inside drawer */
.jbh-drawer-om {
  position: absolute;
  bottom: 24px; right: 24px;
  font-size: 80px;
  color: #C9962A;
  opacity: 0.06;
  pointer-events: none;
  font-family: serif;
  line-height: 1;
  user-select: none;
}

.jbh-drawer-body {
  flex: 1;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.jbh-drawer-divider {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 2px 0;
}
.jbh-drawer-dline { flex: 1; height: 1px; background: #C9962A; opacity: 0.25; }
.jbh-drawer-dsym  { color: #C9962A; font-size: 13px; opacity: 0.7; }

/* Drawer nav links */
.jbh-drawer a.jbh-btn-register,
.jbh-drawer .jbh-btn-login,
.jbh-drawer .jbh-btn-logout {
  width: 100%;
  text-align: center;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
}

/* ── Overlay ── */
.jbh-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 150;
  animation: fadeIn .2s ease;
}
@keyframes fadeIn { from{opacity:0} to{opacity:1} }
`;

/* ─── Component ───────────────────────────────────────────────────────────── */
const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const isAuthenticated = localStorage.getItem("accessToken") !== null;

  /* inject CSS */
  useEffect(() => {
    const id = "jbh-styles";
    if (!document.getElementById(id)) {
      const el = document.createElement("style");
      el.id = id;
      el.textContent = CSS;
      document.head.appendChild(el);
    }
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);

  /* scroll listener */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* lock body scroll when drawer open */
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const gotoLogin = () => {
    navigate("/login");
    setIsOpen(false);
  };
  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    setIsOpen(false);
    navigate("/");
    window.location.reload();
  };

  const AuthBtn = ({ className = "" }) =>
    isAuthenticated ? (
      <button onClick={handleLogout} className={`jbh-btn-logout ${className}`}>
        Logout
      </button>
    ) : (
      <button onClick={gotoLogin} className={`jbh-btn-login  ${className}`}>
        Login
      </button>
    );

  return (
    <>
      {/* ── HEADER BAR ── */}
      <header className={`jbh-header ${scrolled ? "scrolled" : "top"}`}>
        <div className="jbh-inner">
          {/* Logo */}
          <div className="jbh-logo" onClick={() => navigate("/")}>
            <img src={logo} alt="Jain Var Vadhu logo" />
            <div className="jbh-logo-text">
              <span className="jbh-logo-name">Jain Var Vadhu</span>
              <span className="jbh-logo-tagline">
                Divine Values · Sacred Bond
              </span>
            </div>
          </div>

          {/* Phone */}
          <a href="tel:9302210635" className="jbh-phone" aria-label="Call us">
            <FaPhoneAlt aria-hidden="true" />
            <span className="jbh-phone-num">+91 9179333330</span>
          </a>

          {/* Desktop nav */}
          <nav className="jbh-nav-desktop" aria-label="Main navigation">
            <a href="#register-form" className="jbh-btn-register">
              🪷 Register Free
            </a>
            <AuthBtn />
          </nav>

          {/* Mobile: auth always visible */}
          <div className="jbh-mobile-auth">
            <AuthBtn />
          </div>

          {/* Hamburger */}
          <button
            className="jbh-hamburger"
            onClick={() => setIsOpen(true)}
            aria-label="Open menu"
            aria-expanded={isOpen}
          >
            <FaBars size={22} />
          </button>
        </div>
      </header>

      {/* ── DRAWER ── */}
      <div
        className={`jbh-drawer ${isOpen ? "open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="jbh-drawer-head">
          <div>
            <div className="jbh-drawer-title">Jain Var Vadhu</div>
            <div className="jbh-drawer-sub">Trusted by Values</div>
          </div>
          <button
            className="jbh-drawer-close"
            onClick={() => setIsOpen(false)}
            aria-label="Close menu"
          >
            <FaTimes size={20} />
          </button>
        </div>

        <div className="jbh-drawer-body">
          <a
            href="#register-form"
            className="jbh-btn-register"
            onClick={() => setIsOpen(false)}
          >
            🪷 Register Free
          </a>

          <div className="jbh-drawer-divider">
            <div className="jbh-drawer-dline" />
            <span className="jbh-drawer-dsym">☸</span>
            <div className="jbh-drawer-dline" />
          </div>

          <AuthBtn />
        </div>

        {/* decorative Om watermark */}
        <div className="jbh-drawer-om" aria-hidden="true">
          ॐ
        </div>
      </div>

      {/* ── OVERLAY ── */}
      {isOpen && (
        <div
          className="jbh-overlay"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default Header;
